// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { keyBy } from "lodash";

import { StateCode, stateCodes } from "~@jii/configs";
import { StateConfig } from "~@jii/configs";

/**
 * Given all state codes in tuple form, expects a corresponding tuple of config objects
 */
type StateConfigArray<StateCodeList> = StateCodeList extends [
  infer First extends StateCode,
  ...infer Rest,
]
  ? [StateConfig<First>, ...StateConfigArray<Rest>]
  : [];

/**
 * Exhaustive list of {@link StateConfig} objects for all supported states
 */
export const stateConfigs: StateConfigArray<typeof stateCodes.options> = [
  {
    stateCode: "US_AR",
    displayName: "Arkansas",
    urlSlug: "arkansas",
  },
  {
    stateCode: "US_AZ",
    displayName: "Arizona",
    urlSlug: "arizona",
  },
  { stateCode: "US_CO", displayName: "Colorado", urlSlug: "colorado" },
  { stateCode: "US_ID", displayName: "Idaho", urlSlug: "idaho" },
  {
    stateCode: "US_MA",
    displayName: "Massachusetts",
    urlSlug: "mass",
  },
  {
    stateCode: "US_NC",
    displayName: "North Carolina",
    urlSlug: "north-carolina",
  },
  { stateCode: "US_ND", displayName: "North Dakota", urlSlug: "north-dakota" },
  { stateCode: "US_NE", displayName: "Nebraska", urlSlug: "nebraska" },
  {
    stateCode: "US_NYC",
    displayName: "New York City",
    urlSlug: "new-york-city",
  },
  { stateCode: "US_TN", displayName: "Tennessee", urlSlug: "tennessee" },
  { stateCode: "US_UT", displayName: "Utah", urlSlug: "utah" },
];

/**
 * Exhaustive mapping of {@link StateConfig} objects for all supported states
 */
export const stateConfigsByStateCode: {
  [Code in StateCode]: StateConfig<Code>;
} =
  // lodash types broaden keys to string but this should be correct
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  keyBy(stateConfigs, "stateCode") as any;

/**
 * Exhaustive mapping of {@link StateConfig} objects for all supported states
 */
export const stateConfigsByUrlSlug: Record<
  string,
  StateConfig<StateCode> | undefined
> = keyBy(stateConfigs, "urlSlug");
